import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { chatApi } from "../../api/chat";

const scopeKey = (tournamentId?: number | null) =>
  tournamentId != null ? `tournament:${tournamentId}` : "global";

export const chatKeys = {
  all: ["chat"] as const,
  scope: (tournamentId?: number | null) => ["chat", scopeKey(tournamentId)] as const,
  messages: (size: number, tournamentId?: number | null) =>
    ["chat", scopeKey(tournamentId), "messages", size] as const,
  latestTimestamp: (tournamentId?: number | null) =>
    ["chat", scopeKey(tournamentId), "latestTimestamp"] as const,
  pinned: (tournamentId?: number | null) =>
    ["chat", scopeKey(tournamentId), "pinned"] as const,
};

export const useChatMessages = (size: number, tournamentId?: number | null) =>
  useInfiniteQuery({
    queryKey: chatKeys.messages(size, tournamentId),
    queryFn: ({ pageParam = 0 }: { pageParam?: number }) =>
      chatApi.getMessages({ page: pageParam, size, tournamentId }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length === size ? lastPageParam + 1 : undefined,
    // Chat is real-time; a stale cache from a previous mount is worse than one extra fetch.
    refetchOnMount: "always",
  });

export const useChatLatestTimestamp = (tournamentId?: number | null, enabled: boolean = true) =>
  useQuery({
    queryKey: chatKeys.latestTimestamp(tournamentId),
    queryFn: () => chatApi.getLatestTimestamp(tournamentId),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    enabled,
  });

export const useMentionablePlayers = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["chat", "mentionable-players"] as const,
    queryFn: () => chatApi.getMentionablePlayers(),
    staleTime: 5 * 60_000,
    enabled,
  });

export const usePinnedMessages = (tournamentId?: number | null, enabled: boolean = true) =>
  useQuery({
    queryKey: chatKeys.pinned(tournamentId),
    queryFn: () => chatApi.getPinned(tournamentId),
    staleTime: 60_000,
    enabled,
  });
